import { z } from 'zod'

/**
 * Contract for the portal's per-user remote-MCP OAuth proxy
 * (`/api/mcp/oauth/*`). Portal-owned and portal-internal: both the browser
 * client and the same-origin Hono server live in `apps/portal`, so this stays
 * here rather than in `@seen/request-types` (which is for the shared
 * `@seen/react` / `SeenAiApi` tier). The browser owns token custody
 * (localStorage); the server only performs the CORS-blocked discovery /
 * registration / token calls on the user's behalf and never persists anything.
 */

// Wire vocabulary shared by the browser client and the portal server, declared
// once so the header names and callback path can't drift between producer and
// consumer.
export const MCP_OAUTH_CALLBACK_PATH = '/mcp/callback'
// Carries the user's remote-MCP bearer on chat requests, kept separate from the
// Medplum `Authorization` header (different credential, custody, and lifecycle).
export const MCP_AUTHORIZATION_HEADER = 'x-mcp-authorization'
// Server → browser signal that the remote-MCP token was rejected and the user
// must re-authenticate.
export const MCP_STATUS_HEADER = 'x-mcp-status'

// GET /api/mcp/oauth/metadata — everything the browser needs to build the
// authorize redirect and call the token proxy. Resolved server-side via
// RFC 8414 discovery + (cached) dynamic client registration.
export interface McpOAuthMetadataResponse {
  authorizationEndpoint: string
  tokenEndpoint: string
  clientId: string
  scope: string
  // RFC 8707 resource indicator — the VMCP `/mcp` URL the token is bound to.
  resource: string
  // Absolute redirect URI registered for this origin; the browser must use the
  // exact same value in the authorize request and token exchange.
  redirectUri: string
}

// POST /api/mcp/oauth/token — proxied to the MCP authorization server's token
// endpoint. Either an authorization-code exchange or a refresh.
export const mcpOAuthTokenRequestSchema = z.discriminatedUnion('grantType', [
  z.object({
    grantType: z.literal('authorization_code'),
    // The client_id the browser received from /metadata and authorized with;
    // the server forwards it rather than re-registering (DCR is non-idempotent
    // and per-instance, so a fresh registration would mismatch the grant).
    clientId: z.string().min(1),
    code: z.string().min(1),
    codeVerifier: z.string().min(1),
    redirectUri: z.string().url(),
  }),
  z.object({
    grantType: z.literal('refresh_token'),
    clientId: z.string().min(1),
    refreshToken: z.string().min(1),
  }),
])

export type McpOAuthTokenRequest = z.infer<typeof mcpOAuthTokenRequestSchema>

// Raw OAuth 2.0 token response, forwarded verbatim from the authorization
// server. snake_case because it mirrors the wire format the browser persists.
export interface McpOAuthTokenResponse {
  access_token: string
  token_type: string
  expires_in?: number
  refresh_token?: string
  scope?: string
}
